// src/services/clientService.js
/**
 * Client Service - Handles all client-related API communication with backend
 * Backend API Base URL: Production VPS via config.js
 */

import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../config';

const CLIENT_API_URL = `${NODE_BACKEND_API_URL}/clients`;

/**
 * Get all clients with pagination and optional filters
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Records per page (default: 50, max: 500)
 * @param {Object} filters - Optional filters { estado, condicionImpositiva }
 * @returns {Promise} Response with clients list and pagination
 */
export const getAllClients = async (page = 1, pageSize = 50, filters = {}) => {
    try {
        const params = {
            page,
            pageSize
        };

        // Add optional filters
        if (filters.estado) {
            params.estado = filters.estado;
        }
        if (filters.condicionImpositiva) {
            params.condicionImpositiva = filters.condicionImpositiva;
        }

        const response = await axios.get(CLIENT_API_URL, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching all clients:', error);
        throw error;
    }
};

/**
 * Search clients by name (partial match)
 * @param {string} searchTerm - Name to search for
 * @param {number} maxResults - Maximum results to return (default: 50)
 * @returns {Promise} Response with matching clients
 */
export const searchClientsByName = async (searchTerm, maxResults = 50) => {
    try {
        if (!searchTerm || searchTerm.trim().length === 0) {
            return { results: [], total_count: 0 };
        }

        const response = await axios.get(
            `${CLIENT_API_URL}/search-name/${encodeURIComponent(searchTerm)}`,
            { params: { maxResults } }
        );
        return response.data;
    } catch (error) {
        if (error.response && error.response.status === 404) {
            return { results: [], total_count: 0 };
        }
        console.error('Error searching clients by name:', error);
        throw error;
    }
};

/**
 * Search client by CUIT (exact match)
 * @param {string} cuit - CUIT number
 * @returns {Promise} Response with client data or null
 */
export const searchClientByCuit = async (cuit) => {
    try {
        const cleanCuit = cuit.replace(/-/g, ''); // Remove hyphens
        const response = await axios.get(`${CLIENT_API_URL}/search-cuit/${cleanCuit}`);
        return response.data;
    } catch (error) {
        if (error.response && error.response.status === 404) {
            return null;
        }
        console.error('Error searching client by CUIT:', error);
        throw error;
    }
};

/**
 * Validate CUIT with AFIP
 * @param {string} cuit - CUIT number to validate
 * @returns {Promise} Response with AFIP validation data
 */
export const validateCuitWithAfip = async (cuit) => {
    try {
        const cleanCuit = cuit.replace(/[-\s]/g, '');
        
        const response = await axios.post(`${CLIENT_API_URL}/validate-cuit-afip`, {
            cuit: cleanCuit
        });
        
        // Check if response has the expected structure
        if (response.data) {
            // If backend returns { valid: true, data: {...} } format
            if (response.data.valid !== undefined) {
                return response.data;
            }
            
            // If backend returns data directly (as per cuitService pattern)
            // Transform it to match expected format
            return {
                valid: true,
                data: {
                    documento_nro: response.data.documento_nro || cleanCuit,
                    razon_social: response.data.razon_social,
                    condicion_impositiva: response.data.condicion_impositiva,
                    direccion: response.data.direccion || '',
                    localidad: response.data.localidad || '',
                    provincia: response.data.provincia || '',
                    codigopostal: response.data.codigopostal || ''
                }
            };
        }
        
        throw new Error('Invalid response format from AFIP validation');
    } catch (error) {
        console.error('Error validating CUIT with AFIP:', error);
        
        // If it's a 404, the endpoint doesn't exist
        if (error.response && error.response.status === 404) {
            throw new Error('Endpoint de validación AFIP no disponible');
        }
        
        throw error;
    }
};

/**
 * Create new client
 * @param {Object} clientData - Client data object
 * @returns {Promise} Response with created client
 */
export const createClient = async (clientData) => {
    try {
        const response = await axios.post(`${CLIENT_API_URL}/save`, {
            documento_nro: clientData.documento_nro,
            documento_tipo: clientData.documento_tipo || 'CUIT',
            razon_social: clientData.razon_social,
            condicion_impositiva: clientData.condicion_impositiva,
            direccion: clientData.direccion || '',
            localidad: clientData.localidad || '',
            provincia: clientData.provincia || '',
            codigopostal: clientData.codigopostal || '',
            email: clientData.email || '',
            telefono: clientData.telefono || '',
            estado: clientData.estado || 'ACTIVO'
        });
        return response.data;
    } catch (error) {
        console.error('Error creating client:', error);
        throw error;
    }
};

/**
 * Update existing client
 * @param {number} clientId - Client ID
 * @param {Object} updates - Fields to update
 * @returns {Promise} Response with updated client
 */
export const updateClient = async (clientId, updates) => {
    try {
        const response = await axios.put(`${CLIENT_API_URL}/${clientId}`, updates);
        return response.data;
    } catch (error) {
        console.error('Error updating client:', error);
        throw error;
    }
};
